import * as THREE from "three";
import { Palette } from "../scene";
import { plusGeometry } from "./placementMarks";
import { roundedRing, roundedSquare } from "./placementShapes";

/** The placement marks' materials and frame shapes (placement.ts): drawn over the terrain, no depth test, fog or tone mapping. */
const overlay = { transparent: true, depthTest: false, depthWrite: false, fog: false, toneMapped: false, side: THREE.DoubleSide } as const;
/**
 * The build plots lie ON the ground (Kevin, 10-07): depth-tested, pulled a little towards the camera so the turf
 * never pokes through, so a tree or rock in front hides them — and that scenery thins out over them (seeThrough.ts).
 */
const onGround = { ...overlay, depthTest: true } as const;
/**
 * How far an on-ground mark is pulled towards the camera for the depth test (world units): enough that a bump in the
 * turf or a road's kerb beside a plot never cuts it, far less than any tree, rock or tower that stands in front of it.
 */
export const GroundBias = 0.3;
/** A plot material: drawn on the ground, its depth pulled `GroundBias` towards the camera. */
function plot(params: THREE.MeshBasicMaterialParameters): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial({ ...onGround, ...params });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <project_vertex>", `#include <project_vertex>
  mvPosition.xyz += normalize( -mvPosition.xyz ) * ${GroundBias.toFixed(2)};
  gl_Position = projectionMatrix * mvPosition;`);
  };
  m.customProgramCacheKey = () => "td-plot";
  return m;
}
export const validKeyline = new THREE.MeshBasicMaterial({ color: 0x06210f, opacity: 0.55, ...overlay });
/**
 * The legal tower cells as build plots (Kevin, 10-07: the old green cells with white frames read as soft gradients
 * and merged into one quilt): a dark socket inset from the cell, a crisp warm rim and a small plus — flat colours,
 * no fill that breathes, a gap between neighbours. The socket gives the rim contrast on any chapter's ground; the
 * cream stays under the bloom threshold so it never haloes.
 */
export const valid = {
  base: plot({ color: 0x0e1726, opacity: 0.42 }),
  keyline: plot({ color: 0x0b1220, opacity: 0.6 }),
  rim: plot({ color: 0xf6e7bd, opacity: 1 }),
  badge: plot({ color: 0xf0b93a, opacity: 1 }),
  plus: plot({ color: 0x2a1a05, opacity: 1 }),
};
/** The same plots stepped back beside a chosen spot: still there to tap, no longer shouting. */
export const dimmed = {
  base: plot({ color: 0x0e1726, opacity: 0.2 }),
  rim: plot({ color: 0xf6e7bd, opacity: 0.45 }),
  badge: plot({ color: 0xf0b93a, opacity: 0.4 }),
  plus: plot({ color: 0x2a1a05, opacity: 0.4 }),
  keyline: new THREE.MeshBasicMaterial({ color: 0x06210f, opacity: 0.25, ...overlay }),
  frame: new THREE.MeshBasicMaterial({ color: 0xffffff, opacity: 0.4, ...overlay }),
};
/** The rally cells' gold fill and frame (showRallyCells). */
export const rallyFill = new THREE.MeshBasicMaterial({ color: 0xffd75c, opacity: 0.2, ...overlay });
export const rallyFrame = new THREE.MeshBasicMaterial({ color: 0xffe08a, opacity: 0.75, ...overlay });
/** The chosen spot's socket: the plot's dark base, warmed. */
export const spotFill = plot({ color: 0x2a1c06, opacity: 0.5 });
/** The chosen tower spot: its range ring and disc, and the gold frame. */
export const spotRing = new THREE.MeshBasicMaterial({ color: 0xffe08a, opacity: 0.95, ...overlay });
export const spotDisc = new THREE.MeshBasicMaterial({ color: 0xffe08a, opacity: 0.12, ...overlay });
export const spotFrame = plot({ color: 0xffc629, opacity: 1 });
/** The chosen plot's dark keyline, on the ground like the plot. */
export const spotKeyline = plot({ color: 0x06210f, opacity: 0.55 });
/** The Upgrade card's targets: gold frames, bright and pulsing, or stepped back beside a chosen tower. */
export const upgradeFrame = new THREE.MeshBasicMaterial({ color: 0xffc629, opacity: 1, ...overlay });
export const upgradeDim = new THREE.MeshBasicMaterial({ color: 0xffc629, opacity: 0.35, ...overlay });
/** The chosen tower's current range, a faint rim inside the gold ring of the range it will have. */
export const upgradeNowRim = new THREE.MeshBasicMaterial({ color: 0xffffff, opacity: 0.5, ...overlay });
/** The selected tower's range: a translucent disc and a bright rim. */
export const rangeDisc = new THREE.MeshBasicMaterial({ color: Palette.valid, transparent: true, opacity: 0.16, depthWrite: false });
export const rangeRim = new THREE.MeshBasicMaterial({ color: 0xffe08a, transparent: true, opacity: 0.9, depthWrite: false });
/** Rounded square rings in the XY plane like `quad`: the frame (upgrade and rally marks) and the dark keyline round it. */
export const frameGeo = roundedRing(0.45, 0.37, 0.14);
export const keylineGeo = roundedRing(0.49, 0.44, 0.17);
/** The build plot: its socket (0.74 of a cell, so neighbours keep a clear gap), its rim, and the plus. */
export const plotGeo = roundedSquare(0.38, 0.12);
export const plotRimGeo = roundedRing(0.38, 0.31, 0.12);
export const plotKeyGeo = roundedRing(0.41, 0.375, 0.14);
/** The build badge in the plot's middle: a gold coin with a dark plus, readable at phone zoom on any ground. */
export const badgeGeo = new THREE.CircleGeometry(0.17, 28);
export const plusGeo = plusGeometry(0.105, 0.05);
/** The chosen spot: a wider gold rim round a full-cell socket. */
export const spotFrameGeo = roundedRing(0.46, 0.38, 0.14);
export const spotFillGeo = roundedSquare(0.44, 0.13);
/** The Mortar's blind ring (TOWERS_V2 minRange): a red-hatched disc it cannot shoot into, under a red dashed rim. */
export const blindDisc = new THREE.MeshBasicMaterial({ color: 0xd8402e, opacity: 0.2, ...overlay });
export const blindRim = new THREE.MeshBasicMaterial({ color: 0xff6a50, opacity: 0.9, ...overlay });
export const existingRim = new THREE.MeshBasicMaterial({ color: 0xffffff, opacity: 0.32, ...overlay });
